import { useStores } from '../../../stores';
import { useFormik } from 'formik';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Autocomplete, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Role } from '../../../domain/User';
import { Publisher } from '../../../domain/Publisher';
import { useTranslation } from "react-i18next";
import * as yup from 'yup';
import { withLazyMounting } from '../../../components/hoc/LazyDialog';
import { mainUserRoles } from '../userRoleUtils';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

interface FormValues {
    username: string;
    password: string;
    email: string;
    type: string;
    roles: Role[];
    linkedPublisher?: Publisher | undefined;
}

const NewUserDialog = observer((props: Props) => {
    const { t } = useTranslation();
    const { userStore, publisherStore } = useStores();

    const initialValues = {
        username: '',
        password: '',
        email: '',
        type: 'user',
        roles: [],
    } as FormValues;

    const schema = yup.object().shape({
        username: yup.string().required(t("field_is_required", { name: t("username") })),
        password: yup.string().required(t("field_is_required", { name: t("password") }))
            .min(8, t("password_is_too_short", { number: 8 })),
        email: yup.string().email(),
        type: yup.string(),
        roles: yup.array(),
        linkedPublisher: yup.object(),
    });
    const formik = useFormik({
        validationSchema: schema,
        enableReinitialize: true,
        initialValues,
        onSubmit: (values, { resetForm }) => {
            const userData = { ...values } as any;
            if (values.linkedPublisher) {
                userData.linkedPublisher = values.linkedPublisher.id
            }
            userData.roles = [...userData.roles, { name: values.type }];
            userStore.createUser(userData, () => {
                resetForm();
                props.onClose();
            });
        },
    });

    const { errors, values } = formik;
    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>{t("new_user")}</DialogTitle>
                <DialogContent>

                    <TextField
                        margin="normal"
                        id="username"
                        label={t("username")}
                        type="text"
                        fullWidth
                        variant="standard"
                        error={!!errors.username}
                        helperText={errors.username && errors.username as string}
                        value={values.username || ''}
                        onChange={formik.handleChange}
                    />

                    <TextField
                        margin="normal"
                        id="password"
                        label={t("password")}
                        type="password"
                        fullWidth
                        variant="standard"
                        error={!!errors.password}
                        helperText={errors.password && errors.password as string}
                        value={values.password || ''}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        margin="normal"
                        id="email"
                        label={t("email")}
                        type="email"
                        fullWidth
                        variant="standard"
                        error={!!errors.email}
                        helperText={errors.email && errors.email as string}
                        value={values.email || ''}
                        onChange={formik.handleChange}
                    />
                    <FormControl margin="normal" fullWidth>
                        <InputLabel>{t("type")}</InputLabel>
                        <Select
                            fullWidth
                            value={formik.values.type}
                            label={t("type")}
                            onChange={(event: SelectChangeEvent) => {
                                formik.setFieldValue('type', event.target.value as string);
                            }}
                        >
                            <MenuItem value="user">{t("user")}</MenuItem>
                            <MenuItem value="servant">{t("Ministerial Servant")}</MenuItem>
                            <MenuItem value="elder">{t("Elder")}</MenuItem>
                            <MenuItem value="overseer">{t("Circuit Overseer")}</MenuItem>
                            <MenuItem value="secretary">{t("Secretary")}</MenuItem>
                            <MenuItem value="admin">{t("admin")}</MenuItem>
                        </Select>
                    </FormControl>
                    <Autocomplete
                        disablePortal={false}
                        id="roles"
                        multiple
                        options={userStore.roles.filter(role => !mainUserRoles.includes(role.name))}
                        getOptionLabel={(role: Role) => t(role.name)}
                        value={values.roles || []}
                        renderInput={(params) => <TextField
                            {...params}
                            error={!!errors.roles}
                            helperText={errors.roles && errors.roles as string}
                            margin="normal"
                            label={t("roles")}
                        />}
                        onChange={(e, roles) => {
                            formik.setFieldValue("roles", roles)
                        }}
                    />
                    <Autocomplete
                        disablePortal={false}
                        id="linkedPublisher"
                        options={publisherStore.publishers}
                        getOptionLabel={(publisher: Publisher) => publisher.firstName ? `${publisher.lastName} ${publisher.firstName}` : ''}
                        value={values.linkedPublisher || {} as Publisher}
                        renderInput={(params) => <TextField {...params} margin="normal" label={t("linked_publisher")} />}
                        onChange={(e, publisher) => {
                            formik.setFieldValue("linkedPublisher", publisher)
                        }}
                    />

                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("cancel")}</Button>
                    <Button color="primary" type="submit">{t("submit")}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
});

export default withLazyMounting(NewUserDialog);